import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import config from '../config/env';
import { useAuth } from '../hooks/useAuth';
import { useToast } from './ToastContext';
import { useSubscription } from './SubscriptionContext';

const WebSocketContext = createContext();

export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};

export const WebSocketProvider = ({ children }) => {
    const { token, isAuthenticated } = useAuth();
    const { showInfo } = useToast();
    const { plan, isLoading } = useSubscription();
    const canUseWebSocket = plan === 'platinum';
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const socketRef = useRef(null);
    const listenersRef = useRef([]);

    const listenerIdCounterRef = useRef(0);

    // Connect to WebSocket
    const connect = useCallback(async () => {
        if (!isAuthenticated || !token) {
            console.warn('Cannot connect WebSocket: Not authenticated');
            return;
        }

        // Check if user has WebSocket access (Platinum only)
        if (!canUseWebSocket) {
            console.warn('Cannot connect WebSocket: WebSocket feature requires Platinum plan');
            setConnectionError('Real-time updates require Platinum plan. Upgrade to enable this feature.');
            setIsConnected(false);
            return;
        }

        if (socketRef.current?.connected) {
            // Already connected
            setIsConnected(true);
            return;
        }

        return new Promise((resolve, reject) => {
            try {
                // Disconnect existing socket if any
                if (socketRef.current) {
                    socketRef.current.disconnect();
                    socketRef.current = null;
                }

                // Create new socket connection
                const socket = io(config.WEBSOCKET_URL || undefined, {
                    auth: {
                        token: token
                    },
                    transports: ['websocket', 'polling'],
                    reconnection: true,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    reconnectionAttempts: Infinity
                });

                socketRef.current = socket;

                // Connection event handlers
                socket.on('connect', () => {
                    setIsConnected(true);
                    setConnectionError(null);
                    console.log('WebSocket connected', { socketId: socket.id });
                    resolve();
                });

                socket.on('connect_error', (error) => {
                    console.error('WebSocket connection error', error);
                    setIsConnected(false);
                    setConnectionError(error.message || 'Connection failed');
                    reject(error);
                });

                socket.on('disconnect', (reason) => {
                    setIsConnected(false);
                    console.log('WebSocket disconnected', { reason });
                });

                socket.on('error', (error) => {
                    console.error('WebSocket error', error);
                    setConnectionError(error.message || 'WebSocket error');
                });

                // Handle restaurant-message events
                socket.on('restaurant-message', (message) => {
                    console.log('WebSocket message received:', message);

                    // Show toast notification for every message
                    if (message && message.message) {
                        showInfo(message.message);
                    }

                    // Emit to matching registered listeners
                    listenersRef.current.forEach((listener) => {
                        try {
                            // Check if message matches the listener's entity and operation filters
                            console.log(message);
                            console.log(listener);
                            const entityMatch = !listener.entity || message.entity === listener.entity;
                            const operationMatch = !listener.operation || message.operation === listener.operation;

                            if (entityMatch && operationMatch) {
                                listener.callback(message);
                            }
                        } catch (error) {
                            console.error('Error in WebSocket message listener', error);
                        }
                    });
                });

            } catch (error) {
                console.error('Error creating WebSocket connection', error);
                setConnectionError(error.message || 'Connection failed');
                reject(error);
            }
        });
    }, [isAuthenticated, token, showInfo, canUseWebSocket]);

    // Disconnect from WebSocket
    const disconnect = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
            setIsConnected(false);
            setConnectionError(null);
            listenersRef.current = [];
        }
    }, []);

    // Register message handler
    const onMessage = useCallback((entity, operation, callback) => {
        // Support both old signature (callback only) and new signature (entity, operation, callback)
        let actualEntity, actualOperation, actualCallback;

        if (typeof entity === 'function') {
            // Old signature: onMessage(callback)
            actualEntity = null;
            actualOperation = null;
            actualCallback = entity;
        } else {
            // New signature: onMessage(entity, operation, callback)
            actualEntity = entity;
            actualOperation = operation;
            actualCallback = callback;
        }

        if (typeof actualCallback !== 'function') {
            throw new Error('Callback must be a function');
        }

        // Generate unique ID for this listener
        const listenerId = ++listenerIdCounterRef.current;

        const listener = {
            id: listenerId,
            entity: actualEntity,
            operation: actualOperation,
            callback: actualCallback
        };

        listenersRef.current.push(listener);

        // Return unsubscribe function
        return () => {
            const index = listenersRef.current.findIndex(l => l.id === listenerId);
            if (index > -1) {
                listenersRef.current.splice(index, 1);
            }
        };
    }, []);

    // Automatically connect when authenticated
    useEffect(() => {
        // If not authenticated, disconnect
        if (!isAuthenticated || !token) {
            disconnect();
            return;
        }

        // Wait for subscription data before deciding whether to connect
        if (isLoading) {
            return;
        }

        // If user doesn't have WebSocket access, don't connect
        if (!canUseWebSocket) {
            disconnect();
            return;
        }

        // If already connected, do nothing
        if (isConnected) {
            return;
        }

        // Connect to WebSocket
        connect().catch((error) => {
            console.error('Failed to connect WebSocket:', error);
        });
    }, [isAuthenticated, token, isLoading, canUseWebSocket, isConnected, connect, disconnect]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);

    const value = {
        isConnected,
        connectionError,
        connect,
        disconnect,
        onMessage,
        getSocket: () => socketRef.current
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
};
